import React, { useState, useEffect, useCallback, DragEvent } from 'react';
import { useParams } from 'react-router';
import { IconPremiumCrown } from '@sparkol/videoscribe-react-ui';

import { useAppDispatch, useAppSelector } from 'js/store/hooks';
import { ImageCategory, ImageSource } from 'js/types';
import { trackImageProviderChange, trackImageSearch } from 'js/actionCreators/trackingActions';
import { addLibraryImageToCanvas, replaceLibraryImageElement } from 'js/actionCreators/imagesActions';
import { setDragAppResourceToCanvasData } from 'js/shared/helpers/dragAndDropHelpers';
import { updateSearchTabViewed } from 'js/actionCreators/ImageDrawerActions';
import { SearchType } from 'js/reducers/imageSearchReducer';
import useReplaceImage from 'js/shared/hooks/useReplaceImage';
import useUserWithPermissions from 'js/permissions/useUserWithPermissions';
import { SCOPE } from 'js/config/consts';
import { useIsOnlineCallback } from 'js/shared/hooks/useIsOnlineCallback';
import { SEARCH_MODE_CONFIG } from 'js/editor/ImageDrawer/imageSearchModeConfig';

import SearchResultEmptyState from '../../components/EmptyState/SearchResult';
import { useLibraries } from '../../hooks/useLibraries';

import useGetSearchResult from './useGetSearchResult';
import useLoadImages from './useLoadImages';
import useFormatAddImageData from './useFormatAddImageData';

const TAB_TITLES: Record<ImageSource, string> = {
  videoscribe: 'Illustrations',
  'noun-project': 'Icons',
  'streamline-graphics': 'Graphics'
};

const useTabLogic = (
  searchType: SearchType,
  searchInput: string,
  categoryId: string | null,
  onCategoryClick: (category: ImageCategory) => void
) => {
  const user = useUserWithPermissions();
  const dispatch = useAppDispatch();
  const { scribeId } = useParams<{ scribeId: string }>();
  const [selectedTab, setSelectedTab] = useState<ImageSource>('videoscribe');
  const { isInReplaceImageMode, replacingImageElementId } = useReplaceImage();

  const [_, libraries] = useLibraries();
  const currentCategory = categoryId ? libraries.find(lib => lib.id === categoryId) : null;
  const availableSources = currentCategory?.availableSources;

  const activeSearchMode = useAppSelector(state => state.imageSearch.activeSearchMode);

  const { isTabViewed, isTabLoading, getTabImages, getTabResultCount, isTabInit, getAlternativeTab } =
    useGetSearchResult(searchType, searchInput, categoryId);

  const loadImages = useLoadImages(searchType, searchInput, categoryId, selectedTab);

  const formatAddImageData = useFormatAddImageData(searchType, searchInput, categoryId);

  useEffect(() => {
    // Don't load images for a tab excluded by the current search mode —
    // the next effect will switch the tab away, and the load should happen for the new tab instead.
    if (SEARCH_MODE_CONFIG[activeSearchMode].excludedTabs.includes(selectedTab)) return;
    const tabInit = isTabInit(selectedTab);
    if (!tabInit) loadImages(true);
  }, [selectedTab, isTabInit, loadImages, activeSearchMode]);

  useEffect(() => {
    // Switch to Illustrations tab if current tab is not available for the selected category
    if (availableSources && !availableSources.includes(selectedTab)) {
      const defaultTab = availableSources.includes('videoscribe')
        ? 'videoscribe'
        : (availableSources[0] as ImageSource);
      setSelectedTab(defaultTab);
    }
  }, [categoryId, availableSources, selectedTab]);

  useEffect(() => {
    // Switch away from any tab that is excluded by the current search mode
    if (SEARCH_MODE_CONFIG[activeSearchMode].excludedTabs.includes(selectedTab)) {
      setSelectedTab('videoscribe');
    }
  }, [activeSearchMode, selectedTab]);

  const handleTabChange = (tabId: string) => {
    const selectedTabId = tabId as ImageSource;
    setSelectedTab(selectedTabId);
    const provider = selectedTabId === 'videoscribe' ? 'library' : 'noun-project';
    dispatch(trackImageProviderChange(provider, searchType, selectedTabId));
    if (!isTabViewed(selectedTabId)) {
      dispatch(updateSearchTabViewed(selectedTabId, searchInput, true));
      dispatch(trackImageSearch(searchInput, parseInt(scribeId), selectedTabId));
    }
  };

  const addImageToCanvas = useCallback(
    (selectedTab: ImageSource, imageId: string | number) => {
      const addImageData = formatAddImageData(selectedTab, imageId);
      if (!addImageData) return;

      if (isInReplaceImageMode && replacingImageElementId) {
        return dispatch(
          replaceLibraryImageElement({
            imageId: addImageData.imageId,
            imageType: addImageData.contentType,
            scribeId: addImageData.scribeId,
            provider: addImageData.provider,
            sourceType: addImageData.sourceType,
            canvasSize: addImageData.canvasSize,
            selectedLibrary: addImageData.category ?? '',
            filename: addImageData.filename,
            elementId: replacingImageElementId,
            isPremium: addImageData.isPremium
          })
        );
      }

      dispatch(
        addLibraryImageToCanvas(
          addImageData.imageId,
          addImageData.contentType,
          addImageData.sourceType,
          addImageData.scribeId,
          addImageData.canvasSize,
          addImageData.category,
          addImageData.filename,
          addImageData.provider,
          addImageData.isPremium
        )
      );
    },
    [dispatch, formatAddImageData, isInReplaceImageMode, replacingImageElementId]
  );

  const handleImageDrag = useCallback(
    (e: DragEvent<HTMLButtonElement>, selectedTab: ImageSource, imageId: string | number) => {
      const addImageData = formatAddImageData(selectedTab, imageId);
      if (!addImageData) return;
      setDragAppResourceToCanvasData(e, addImageData);
    },
    [formatAddImageData]
  );

  const handleClickImage = useIsOnlineCallback((tabId: ImageSource, imageId: string | number) =>
    addImageToCanvas(tabId, imageId)
  );

  const createTabConfig = useCallback(
    (tabId: ImageSource) => {
      let icon;

      if ((tabId === 'noun-project' || tabId === 'streamline-graphics') && !user.can([SCOPE.usePremiumContent])) {
        icon = <IconPremiumCrown />;
      }

      return {
        id: tabId,
        title: TAB_TITLES[tabId],
        count: getTabResultCount(tabId),
        icon,
        images: getTabImages(tabId),
        onLoadMore: loadImages,
        isLoading: isTabLoading(tabId),
        onClickImage: (imageId: string | number) => handleClickImage(tabId, imageId),
        onDragImage: (e: DragEvent<HTMLButtonElement>, imageId: string | number) => handleImageDrag(e, tabId, imageId),
        searchInput,
        categoryId,
        emptyState: (
          <SearchResultEmptyState
            tab={TAB_TITLES[tabId]}
            alternativeTab={getAlternativeTab(tabId)}
            onCategoryClick={onCategoryClick}
          />
        )
      };
    },
    [
      user,
      getTabResultCount,
      getTabImages,
      loadImages,
      isTabLoading,
      searchInput,
      categoryId,
      getAlternativeTab,
      onCategoryClick,
      handleClickImage,
      handleImageDrag
    ]
  );

  const configTabs = useCallback(() => {
    const { excludedTabs } = SEARCH_MODE_CONFIG[activeSearchMode];
    const shouldShowTab = (source: ImageSource) => {
      if (excludedTabs.includes(source)) {
        return false;
      }
      if (availableSources) {
        const included = availableSources.includes(source);
        return included;
      }
      return true;
    };

    const tabs = [
      ...(shouldShowTab('videoscribe') ? [createTabConfig('videoscribe')] : []),
      ...(shouldShowTab('noun-project') ? [createTabConfig('noun-project')] : []),
      ...(shouldShowTab('streamline-graphics') ? [createTabConfig('streamline-graphics')] : [])
    ];
    return tabs;
  }, [createTabConfig, availableSources, activeSearchMode]);

  return {
    selectedTab,
    handleTabChange,
    configTabs
  };
};

export default useTabLogic;
